/**
 * @file PublishViewSection.tsx
 * @description Часть «Вид сайта» окна публикации (phase19.16 Р1, Р1-6а;
 * uiux-design-r1 §6, uiux-builder-shell.md §3.1): что изменится по частям
 * (кто и когда правил), чьи правки ждут и у кого есть право (SB60), проверки
 * перед публикацией. Рядом стоят страницы с правками (Р3а) — тогда у
 * вида сайта флажок «для всех страниц и экранов магазина».
 *
 * phase19.16 Р2а: изменённый шаблон страницы товара называется по охвату —
 * «Изменён шаблон страницы товара (N товаров)»; у проверок шаблона —
 * «Вернуть элемент» (`onRestore`).
 *
 * SB125: при ошибках проверок после списка — галочка «публикую с
 * незакрытыми проверками» (`PublishAcknowledgeRow`); пока она не стоит —
 * «Черновик сохранён — опубликуете, когда исправите».
 */

import type { ProductRequiredKind } from "@chaiberi/shared";

import { SectionLabel } from "../../ui/SectionLabel";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { ChangeItemsList } from "../ChangeItemsList";
import { CheckRow } from "../CheckRow";
import { useSiteView } from "../site-view-context";
import { PublishAcknowledgeRow } from "./PublishAcknowledgeRow";
import { PublishChecksList } from "./PublishChecksList";
import { blockedExplanation, formatEditMoment, partHeading } from "../../../lib/site-view/changes";
import { pluralRu } from "../../../lib/plural";
import { useTemplateAppliesTo } from "../../../hooks/site-builder/useTemplateAppliesTo";
import type { SiteChangedPart, SiteViewChanges } from "../../../lib/site-view/types";

interface Props {
  snapshot: SiteViewChanges;
  /** Показать флажок (рядом пункт страницы). */
  selectable: boolean;
  checked: boolean;
  disabled: boolean;
  onChecked: (value: boolean) => void;
  /** Сколько проверок мешают публикации. */
  errors: number;
  /** «Вернуть элемент» шаблона страницы товара. */
  onRestore?: (kind: ProductRequiredKind) => Promise<void>;
  /** SB125: галочка «публикую с незакрытыми проверками»; нет — галочки нет. */
  acknowledge?: { checked: boolean; onChange: (value: boolean) => void; allowed: boolean };
}

/** Заголовок части: у шаблона товара — с охватом (uiux-design-r2a §7). */
function partTitle(part: SiteChangedPart, appliesTo: number | null): string {
  if (part.part !== "PRODUCT_TEMPLATE") return partHeading(part.label);
  return appliesTo === null
    ? "Изменён шаблон страницы товара (все товары на сайте)"
    : `Изменён шаблон страницы товара (${appliesTo} ${pluralRu(appliesTo, ["товар", "товара", "товаров"])})`;
}

/** Почему вид сайта сейчас не опубликовать (пункт неактивен); `null` — объяснит галочка ниже. */
function disabledReason(errors: number, blocked: number, canAcknowledge: boolean): string | null {
  if (blocked > 0) return "Часть правок вида публикует сотрудник с нужным правом — подробности ниже.";
  if (errors > 0) return canAcknowledge ? null : "Сейчас не опубликовать: исправьте проверки ниже. Страницы можно опубликовать отдельно.";
  return "Публиковать нечего.";
}

export function PublishViewSection({ snapshot, selectable, checked, disabled, onChecked, errors, onRestore, acknowledge }: Props) {
  const view = useSiteView();
  // Число товаров — с сервера при открытии окна (не только на экране шаблона).
  const appliesTo = useTemplateAppliesTo(snapshot.changedParts.some((p) => p.part === "PRODUCT_TEMPLATE"));
  const total = snapshot.changedParts.reduce((sum, p) => sum + p.count, 0);
  const checksShown = !(selectable && !checked && !disabled);
  // Галочка SB125 — только когда мешают одни проверки: чужие правки она не снимает.
  const showAcknowledge = !!acknowledge && errors > 0 && snapshot.changedParts.length > 0 && snapshot.blockedBy.length === 0;
  const reason = disabled ? disabledReason(errors, snapshot.blockedBy.length, showAcknowledge) : null;
  return (
    <div className="flex flex-col gap-4">
      {selectable ? (
        <div className="flex flex-col gap-1">
          <CheckRow checked={checked} disabled={disabled} onChange={onChecked}>
            Вид сайта · {total} {pluralRu(total, ["изменение", "изменения", "изменений"])} — для всех страниц и экранов магазина
          </CheckRow>
          {/* Пункт неактивен — причина сразу под ним, проверки ниже. */}
          {reason ? <p className="type-caption pl-7 text-on-surface-variant">{reason}</p> : null}
        </div>
      ) : (
        <SectionLabel as="h2">Что изменится</SectionLabel>
      )}
      {snapshot.changedParts.length === 0 ? (
        <p className="type-body text-on-surface-variant">Нечего публиковать — черновик совпадает с сайтом.</p>
      ) : (
        snapshot.changedParts.map((part) => (
          <div key={part.part} className="flex flex-col gap-1">
            <p className="type-card-title text-on-surface">
              {partTitle(part, appliesTo)} · {part.count}
              {part.editedBy ? (
                <span className="type-caption font-normal text-on-surface-variant">
                  {" "}
                  ({part.editedBy.name}
                  {part.editedAt ? `, ${formatEditMoment(part.editedAt)}` : ""})
                </span>
              ) : null}
            </p>
            <ChangeItemsList items={part.items} limit={6} />
          </div>
        ))
      )}
      {snapshot.blockedBy.length > 0 ? <NoticeStrip tone="error">{blockedExplanation(snapshot, view.currentUserId)}</NoticeStrip> : null}
      {/* Пункт сняли сами — его проверки не мешают публикации страницы, не показываем. */}
      {checksShown ? (
        <>
          <SectionLabel as="h2">Проверки</SectionLabel>
          <PublishChecksList checks={snapshot.checks} onRestore={onRestore} />
        </>
      ) : null}
      {checksShown && showAcknowledge && acknowledge ? (
        <PublishAcknowledgeRow
          errors={snapshot.checks.filter((c) => c.level === "error")}
          checked={acknowledge.checked}
          onChange={acknowledge.onChange}
          allowed={acknowledge.allowed}
        />
      ) : null}
      {errors > 0 && checksShown && !(showAcknowledge && acknowledge?.checked) ? (
        <p className="type-caption text-on-surface-variant">Черновик сохранён — опубликуете, когда исправите.</p>
      ) : null}
    </div>
  );
}
